// i18n: translated
/**
 * 管理画面 shell の表示言語 Provider (admin-english-toggle-20260924 / ADR-081)。
 *
 * `LocaleProvider` (i18n の中心、通信を知らない) に、サーバー個人設定との同期と
 * 保存失敗のトーストを配線する。`AppLayout` が認証済みの shell 全体を包む。
 *
 * - 端末に保存が無いとき: サーバー個人設定の表示言語で始める (別の PC で選んだ言語を引き継ぐ)
 * - 切替時: 端末に保存し、サーバー個人設定にも保存する。失敗したら warning トースト
 *   (切替自体はこのブラウザで有効なまま)
 * - ops surface (Platform Operator Console) ではサーバー同期をしない (テナントの個人設定を
 *   前提にしない)。端末内保存と切替は同じように使える
 * - 表示中の言語を端末の「最後に使った言語」としても記録する (ログイン画面がその言語で描く。p2-28)
 */
import { useCallback, type ReactNode } from 'react';
import { LocaleProvider, translate, type Locale } from '@/i18n';
import { LastUsedLocaleRecorder } from '@/i18n/lastUsedLocale';
import { useLocalePreference } from '@/hooks/useLocalePreference';
import { useToast } from '@/hooks/useToast';

/**
 * ops surface 判定。App.tsx の detectSurfaceMode / Sidebar の IS_OPS_SURFACE と同一規則
 * (App → AppLayout → 本 component の import 循環を避けるためローカルに持つ。変更時は同期する)。
 */
const IS_OPS_SURFACE = /^(dev-)?ops\./.test(window.location.hostname);

interface AdminLocaleProviderProps {
  /** ログイン中の利用者 ID (端末内保存を職員ごとに分ける) */
  userId: string | null | undefined;
  children: ReactNode;
}

export default function AdminLocaleProvider({ userId, children }: AdminLocaleProviderProps) {
  const { showToast } = useToast();
  const { loadServerLocale, saveServerLocale } = useLocalePreference();

  // 失敗した時点の切替先の言語で知らせる (英語へ切り替えた直後なら英語で出す)。
  const handleRemoteSaveError = useCallback(
    (locale: Locale) => {
      showToast('warning', translate(locale, 'shell.language.saveFailed'));
    },
    [showToast],
  );

  return (
    <LocaleProvider
      storageScope={userId}
      loadRemoteLocale={IS_OPS_SURFACE ? undefined : loadServerLocale}
      saveRemoteLocale={IS_OPS_SURFACE ? undefined : saveServerLocale}
      onRemoteSaveError={handleRemoteSaveError}
    >
      {/* p2-28: ログイン画面を端末の最後に使った言語で描くため、表示中の言語を記録する */}
      <LastUsedLocaleRecorder />
      {children}
    </LocaleProvider>
  );
}
